import { useState, useEffect, useMemo } from 'react';
import { FirebaseProvider } from '../contexts/FirebaseContext';
import { useSurveyResults } from '../hooks/useSurveyResults';
import { useTranslation } from '../hooks/useTranslation';
import { useSlideshowTiming } from '../hooks/useSlideshowTiming';
import Chart from './charts/Chart';
import CallToAction from './CallToAction';
import './ResultsPage.css';

function ResultsContent() {
  const { stats, refreshData } = useSurveyResults();
  const { translations } = useTranslation();
  const { calculateSlideDuration, getCtaDuration, shouldShowCta } = useSlideshowTiming();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSlide, setShowSlide] = useState(false);

  // Get questions arrays - memoize to prevent recreating on every render
  const questionsEn = useMemo(() => Object.keys(translations.questions.en), [translations.questions.en]);
  const questionsFr = useMemo(() => Object.keys(translations.questions.fr), [translations.questions.fr]);
  const answersEn = useMemo(() => Object.values(translations.questions.en) as string[][], [translations.questions.en]);
  const answersFr = useMemo(() => Object.values(translations.questions.fr) as string[][], [translations.questions.fr]);
  const totalQuestions = questionsEn.length;

  useEffect(() => {
    if (!stats || totalQuestions === 0) return;

    let timeoutIds: number[] = [];

    const nextSlide = () => {
      // Calculate duration for current question
      const questionText = questionsEn[currentIndex];
      const answers = answersEn[currentIndex];

      const slideDuration = calculateSlideDuration(questionText, answers);
      // if CTA should show, duration is longer than slideDuration
      // (slide is hidden at the end of slideDuration, revealing the CTA underneath for the rest of the time)
      const duration = shouldShowCta(currentIndex) ? slideDuration + getCtaDuration() : slideDuration;

      const id1 = window.setTimeout(() => {
        setShowSlide(false);

      }, slideDuration);
      timeoutIds.push(id1);

      const id2 = window.setTimeout(() => {
        const nextIndex = (currentIndex + 1) % totalQuestions;
        setCurrentIndex(nextIndex);

        // Refresh data only at the beginning of each cycle for privacy
        if (nextIndex === 0) {
          refreshData();
        }
      }, duration);
      timeoutIds.push(id2);

      setShowSlide(true);
    };

    nextSlide();

    return () => {
      if (timeoutIds.length) {
        timeoutIds.forEach(id => window.clearTimeout(id));
      };
    };
  }, [currentIndex, stats, totalQuestions, questionsEn, answersEn, calculateSlideDuration, getCtaDuration, shouldShowCta, refreshData]);


  const currentQuestionCount = stats.responsesByQuestion[currentIndex] || {};

  if (!stats || !stats.responsesByQuestion || stats.totalResponses === 0) {
    return null;
  }

  return (
    <div className={`results-slideshow ${showSlide ? '' : 'hidden'}`}>
      <div className="results-container">
        <div className="result-column">
          <Chart
            questionNumber={currentIndex}
            questionText={questionsFr[currentIndex]}
            answers={answersFr[currentIndex]}
            count={currentQuestionCount}
            totalRespondents={stats.totalResponses}
            showCount={false}
          />
          <div className="chart-info">
            {stats.totalResponses}
            {translations.ui.fr.tally}
          </div>
        </div>
        <div className="result-column">
          <Chart
            questionNumber={currentIndex}
            questionText={questionsEn[currentIndex]}
            answers={answersEn[currentIndex]}
            count={currentQuestionCount}
            totalRespondents={stats.totalResponses}
            showCount={false}
          />
          <div className="chart-info">
            {stats.totalResponses}
            {translations.ui.en.tally}
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultsPage() {
  return (
    <FirebaseProvider>
      <CallToAction />
      <ResultsContent />
    </FirebaseProvider>
  );
}

export default ResultsPage;
